import React, {useEffect, useMemo, useState} from "react";
import '../../css/pageBsi.css';
import {API_BSI_DIAGRAM} from "../../../service/api.endpoint";
import moment from "moment";
// @ts-ignore
import CanvasJSReact from '@canvasjs/react-charts';
import {useQuery} from "@tanstack/react-query";
import * as S from "./ modal_bsi_diagram_styled";
import http from "../../../service/axiosConfig";


var CanvasJSChart = CanvasJSReact.CanvasJSChart;
const MAX_DIAGRAM_POINTS = 2500

export type Item = {
    label: string;
    id: string;
};

interface ModalComponentProps {
    isOpen: boolean;
    onClose: any;
    equipageInfo?: string
    fileName?: any
    bsi?: any,
    bsiFileDiagram?: any,
    localFileName?: string,
    localFileReading?: boolean,
}


// ####################
// SORT DIAGRAM DATA
// ####################
interface Data {
    x: string;
    y: number;
}

function compareDescending(a: Data, b: Data) {
    if (a.x < b.x) {
        return -1;
    }
    if (a.x > b.x) {
        return 1;
    }
    return 0;
}

function downsamplePoints(points: any[], maxPoints: number) {
    if (!Array.isArray(points) || points.length <= maxPoints) {
        return points || []
    }

    const step = Math.ceil(points.length / maxPoints)
    const result = points.filter((_: any, index: number) => index % step === 0)
    const lastPoint = points[points.length - 1]

    if (result[result.length - 1] !== lastPoint) {
        result.push(lastPoint)
    }

    return result
}

// ###################

const ModalBsiDiagram: React.FC<ModalComponentProps> = ({
                                                            fileName,
                                                            isOpen,
                                                            onClose,
                                                            equipageInfo,
                                                            bsiFileDiagram,
                                                            bsi,
                                                            localFileName,
                                                            localFileReading,
                                                        }) => {
    const [dataDiagram, setDataDiagram] = useState<any>(null)
    const {isPending, error, data, refetch} = useQuery({
        queryKey: ['diagramData', fileName],
        enabled: Boolean(fileName) && !bsiFileDiagram,
        queryFn: async () => {
            try {
                const response = await http.get(API_BSI_DIAGRAM, {params: {fileName}});
                setDataDiagram(response.data);
                return response.data;
            } catch (error) {
                console.error('Произошла ошибка при выполнении запроса:', error);
                throw error;
            }
        },
    });


    const handleNextRequest = () => {
        if (bsiFileDiagram || !fileName) {
            return
        }
        refetch().then((r: any) => r.data)
    }

    useEffect(() => {
        if (bsiFileDiagram) {
            setDataDiagram(bsiFileDiagram)
            return
        }

        // Обновление данных при следующем запросе
        handleNextRequest()
    }, [fileName, bsiFileDiagram]);


    const diagramStats = useMemo(() => {
        if (!dataDiagram || dataDiagram === 'File not found') {
            return null
        }

        if (bsiFileDiagram) {
            const rows = bsiFileDiagram.split('\n').slice(3).filter(Boolean).length
            return {
                rows,
                rendered: Math.min(rows, MAX_DIAGRAM_POINTS),
            }
        }

        const rows = Array.isArray(dataDiagram?.depth) ? dataDiagram.depth.length : 0
        return {
            rows,
            rendered: Math.min(rows, MAX_DIAGRAM_POINTS),
        }
    }, [bsiFileDiagram, dataDiagram])

    const options = useMemo(() => {
        if (!dataDiagram || dataDiagram === 'File not found') {
            return null
        }

        let data = null;
        if (bsiFileDiagram) {
            const transformedArray = {
                depth: [] as any[],
                speed: [] as any[],
                tension: [] as any[]
            };

            bsiFileDiagram.split('\n').slice(3).forEach((item: any) => {
                const [date, value1, value2, value3] = item.split("\t");
                const formattedDate = moment(date, "DD.MM.YYYY HH:mm:ss:SSS").format("YYYY-MM-DDTHH:mm:ss.SSS");

                const dateObject = new Date(formattedDate);

                const obj1 = {
                    x: dateObject,
                    y: Math.abs(parseInt(value1))
                };

                const obj2 = {
                    x: dateObject,
                    y: parseInt(value3) / 10
                };

                const obj3 = {
                    x: dateObject,
                    y: parseInt(value2)
                };

                transformedArray.depth.push(obj1);
                transformedArray.speed.push(obj2);
                transformedArray.tension.push(obj3);

            });

            data = {
                depth: transformedArray.depth.sort(compareDescending),
                speed: transformedArray.speed.sort(compareDescending),
                tension: transformedArray.tension.sort(compareDescending),
            }
        } else data = {
            depth: dataDiagram.depth.sort(compareDescending),
            speed: dataDiagram.speed.sort(compareDescending),
            tension: dataDiagram.tension.sort(compareDescending),
        }
        const transformedArrayDepth = downsamplePoints(data['depth'], MAX_DIAGRAM_POINTS).map((item: any) => {
            // noise filter
            if (item.y > 5000) {
                item.y = 0
            }
            let lineColor;
            if (bsi && bsi.length > 0) {
                const time = moment(item.x, 'DD.MM.YYYY HH:mm:ss')
                lineColor = bsi.some((interval: any) => {
                    const start = moment(interval.descent_start)
                    const end = moment(interval.lift_end)
                    return time.isBetween(start, end, null, '[]'); // проверяем, попадает ли время в любой из интервалов в массиве
                }) ? 'red' : '#0073BB';
            }
            return {
                ...item,
                x: moment(item.x, 'DD.MM.YYYY HH:mm:ss').toDate(),
                // lineColor: 'red'
                lineColor: bsi && bsi.length > 0 && lineColor

            };
        });
        const transformedArraySpeed = downsamplePoints(data['speed'], MAX_DIAGRAM_POINTS).map((item: any) => {
            // noise filter
            if (item.y > 2000) {
                item.y = 2000
            }
            return {
                ...item,
                x: moment(item.x, 'DD.MM.YYYY HH:mm:ss').toDate(),
                lineColor: '#64A8DD'

            };
        });
        const transformedArrayTension = downsamplePoints(data['tension'], MAX_DIAGRAM_POINTS).map((item: any) => {
            item.y = item.y * 5
            // noise filter
            if (item.y > 3000) {
                item.y = 1500
            }
            return {
                ...item,
                x: moment(item.x, 'DD.MM.YYYY HH:mm:ss').toDate(),
                lineColor: '#63ee45'

            };
        });
        return {
            animationEnabled: false,
            exportEnabled: true,
            zoomEnabled: true,
            title: {
                text: `Диаграмма БСИ ${equipageInfo}`
            },
            axisX: {
                title: "Дата, время",
            },
            axisY: {
                title: "Глубина/скорость/натяжение",
            },
            data: [
                {
                    type: "spline",
                    showInLegend: true,
                    name: "Натяжение",
                    color: '#63ee45',
                    lineThickness: 2,
                    xValueFormatString: "DD.MM.YYYY HH:mm:ss",
                    dataPoints: transformedArrayTension
                },
                {
                    type: "spline",
                    showInLegend: true,
                    name: "Скорость",
                    color: '#64A8DD',
                    lineThickness: 1,
                    xValueFormatString: "DD.MM.YYYY HH:mm:ss",
                    dataPoints: transformedArraySpeed
                },
                {
                    type: "stackedArea",
                    showInLegend: true,
                    name: "Глубина",
                    xValueFormatString: "DD.MM.YYYY HH:mm:ss",
                    color: '#0073BB',
                    lineThickness: 4,
                    dataPoints: transformedArrayDepth
                },]
        }
    }, [bsi, bsiFileDiagram, dataDiagram, equipageInfo])

    const handleOnClose = () => {
        bsiFileDiagram = null
        setDataDiagram(null)
        bsi = null
        onClose()
    }

    return (
        <S.StyledModal
            isOpen={isOpen}
            hasOverlay
            onEsc={handleOnClose}
        >
            <S.DiagramModalContainer>
                <S.DiagramHeader>
                    <div>
                        <S.DiagramTitle>
                            Диаграмма БСИ
                        </S.DiagramTitle>
                        {equipageInfo && (
                            <S.DiagramSubtitle>
                                {equipageInfo}
                            </S.DiagramSubtitle>
                        )}
                        {localFileName && (
                            <S.DiagramSubtitle>
                                Файл: {localFileName}
                            </S.DiagramSubtitle>
                        )}
                    </div>
                </S.DiagramHeader>

                <S.DiagramContent>
                    {localFileReading ? (
                        <S.LoaderContainer>
                            <S.LoaderIcon>⏳</S.LoaderIcon>
                            <S.LoaderText>Читаем файл БСИ...</S.LoaderText>
                        </S.LoaderContainer>
                    ) : isPending || !dataDiagram ? (
                        <S.LoaderContainer>
                            <S.LoaderIcon>⏳</S.LoaderIcon>
                            <S.LoaderText>Готовим диаграмму БСИ...</S.LoaderText>
                        </S.LoaderContainer>
                    ) : dataDiagram === 'File not found' ? (
                        <S.NoDataMessage>
                            <S.NoDataText>НЕТ ДАННЫХ ДЛЯ ЭТОГО БСИ</S.NoDataText>
                        </S.NoDataMessage>
                    ) : options ? (
                        <>
                            {diagramStats && (
                                <S.DiagramInfo>
                                    Строк в файле: {diagramStats.rows}. На графике показано точек: {diagramStats.rendered}.
                                </S.DiagramInfo>
                            )}
                            <S.ChartWrapper>
                                <CanvasJSChart options={options}/>
                            </S.ChartWrapper>
                        </>
                    ) : (
                        <S.NoDataMessage>
                            <S.NoDataText>НЕТ ДАННЫХ ДЛЯ ЭТОГО БСИ</S.NoDataText>
                        </S.NoDataMessage>
                    )}
                </S.DiagramContent>

                <S.DiagramFooter>
                    <S.StyledButton
                        size="m"
                        view="primary"
                        label="Закрыть"
                        onClick={handleOnClose}
                    />
                </S.DiagramFooter>
            </S.DiagramModalContainer>
        </S.StyledModal>
    )
};

export default ModalBsiDiagram;
